"use client";

import * as React from "react";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex gap-8 animate-pulse">
        {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-4 w-20 bg-muted rounded" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu className="hidden lg:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || item.url || "#"} className="text-[14px] font-bold uppercase tracking-widest text-[#1a1c23] hover:text-[#FFB6C1] transition-all relative group py-2">
                {item.label}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-[2px] bg-[#FFB6C1] transition-all duration-300 group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return <div className="p-4 text-sm text-muted-foreground font-sans uppercase">Loading menu...</div>;
  return <nav className="flex flex-col gap-2 p-3">
      {menuItems.map((item, i) => <Link key={item.id} to={item.href || item.url || "#"} className="text-base font-bold uppercase tracking-tight text-foreground hover:text-[#FFB6C1] hover:bg-accent p-3 rounded-md transition-colors" onClick={onClose} data-index={i}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function PrimaryStationHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="elite-header" className={`sticky top-0 z-50 w-full border-b border-transparent transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-md border-border/50 shadow-md" : "bg-background"}`}>
      <div className="container mx-auto px-3 sm:px-4 h-16 md:h-20 flex items-center justify-between gap-4">
        <div className="flex-shrink-0">
          <Link to="/">
            <WvcLogo className="h-8 md:h-10 w-auto" />
          </Link>
        </div>

        <MenuProvider menu_id="22" className="hidden lg:block">
          <HeaderMenuConsumer />
        </MenuProvider>

        <div className="flex items-center gap-1.5 sm:gap-2">
          <Button variant="ghost" size="icon" className="lg:hidden text-[#1a1c23] hover:text-[#FFB6C1]" onClick={() => setIsMobileMenuOpen(true)}>
            <Menu className="size-6" />
            <span className="sr-only">Menu</span>
          </Button>
        </div>
      </div>

      {isMobileMenuOpen && <div className="fixed inset-0 z-[60] bg-background lg:hidden animate-in slide-in-from-right duration-300 flex flex-col">
          <div className="flex items-center justify-between p-4 border-b">
            <span className="font-bold uppercase tracking-widest text-lg">Menu</span>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)}>
              <X className="size-6" />
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto">
            <MenuProvider menu_id="22">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>}
    </header>;
}